// client/src/coms/views/games_parts/GamesCatalogue.js
// The Voxemera Activities catalogue: every activity in one list, grouped as Games (board, card, arcade,
// party and real-time games, Texas Hold'em), Watch (Watch Together) and Create (the Canvas drawing games and
// the classic canvas game); the player's own activities, those open on this server, joining by invitation
// code, party stats and recent galleries. Presentational; GamesHub owns data.
import React, { useMemo, useState } from 'react';
import UserAvatar from './UserAvatar';
import { MODE_ICONS } from '../canvas_parts/party/partyMeta';

const GROUPS = ['All Activities', 'Games', 'Watch', 'Create'];
const GAME_CATEGORIES = ['Arcade', 'Board', 'Cards', 'Party', 'Real-time'];

// Which top-level group an activity belongs to. Presentation only: ids and categories are unchanged.
export function groupOf(entry) {
  if (entry.kind === 'watch' || entry.category === 'Watch') return 'Watch';
  if (entry.kind === 'party' || entry.kind === 'canvas' || entry.category === 'Drawing') return 'Create';
  return 'Games';
}

// Callers still pass the old category names ('All', 'Drawing', 'Arcade'…).
function initialSelection(category) {
  if (!category || category === 'All' || category === 'All Activities') return { group: 'All Activities', sub: '' };
  if (category === 'Drawing' || category === 'Create') return { group: 'Create', sub: '' };
  if (category === 'Watch') return { group: 'Watch', sub: '' };
  if (category === 'Games') return { group: 'Games', sub: '' };
  return { group: 'Games', sub: category };
}

export function playersLabel(entry) {
  if (entry.kind === 'watch') return 'Anyone with the code · voice optional';
  return entry.minPlayers === entry.maxPlayers ? `${entry.maxPlayers} players` : `${entry.minPlayers}–${entry.maxPlayers} players`;
}

function entryIcon(entry) {
  if (entry.kind === 'party') return MODE_ICONS[entry.icon] || '🎨';
  if (entry.kind === 'canvas') return '✏️';
  if ((entry.arcade || entry.kind === 'watch' || entry.key === 'holdem') && entry.icon) return entry.icon;
  return '🎲';
}

function RulesDialog({ entry, onClose, onHost }) {
  return (
    <div className="party-modal-backdrop" role="dialog" aria-modal="true" aria-label={`How to play ${entry.name}`} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="party-modal" style={{ '--party-accent': entry.accent || '#5865f2' }}>
        <div className="party-modal-head">
          <span className="party-card-icon" aria-hidden="true">{entryIcon(entry)}</span>
          <div>
            <div className="party-kicker">{entry.type}</div>
            <h3>{entry.name}</h3>
          </div>
        </div>
        <p>{entry.description || entry.blurb}</p>
        {entry.rules && entry.rules.length ? <ol className="party-rule-list">{entry.rules.map((rule) => <li key={rule}>{rule}</li>)}</ol> : null}
        {entry.modes && entry.modes.length ? (
          <div className="party-setting multi">
            <span>Modes</span>
            <div className="party-chips">{entry.modes.map((mode) => <span key={mode.id} className="party-chip" title={mode.description}>{mode.label}</span>)}</div>
          </div>
        ) : null}
        <div className="party-row end">
          <button type="button" className="party-btn ghost" onClick={onClose}>Close</button>
          <button type="button" className="party-btn" onClick={() => onHost(entry)}>{entry.kind === 'watch' ? 'Start watching' : 'Host this game'}</button>
        </div>
      </div>
    </div>
  );
}

function GameCard({ entry, openCount, onHost, onRules, onJoin, index }) {
  return (
    <article className="party-card" style={{ '--party-accent': entry.accent || '#5865f2', animationDelay: `${Math.min(index, 20) * 30}ms` }} data-testid={`game-card-${entry.key}`}>
      <div className="party-card-art" aria-hidden="true" style={entry.cover ? { backgroundImage: `url(${entry.cover})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}>
        {entry.cover ? null : <span className="party-card-icon">{entryIcon(entry)}</span>}
        <span className="party-card-genre">{entry.type}</span>
      </div>
      <div className="party-card-body">
        <h3>{entry.name}</h3>
        {entry.kind === 'party' ? <div className="party-card-tagline">{entry.blurb}</div> : null}
        <p>{entry.kind === 'party' ? entry.description : entry.blurb}</p>
        <div className="party-card-meta">
          <span title="Players">👥 {playersLabel(entry)}</span>
          {entry.minutes ? <span title="Duration">⏱ ~{entry.minutes} min</span> : null}
          {entry.difficulty ? <span title="Difficulty" className={`party-difficulty ${String(entry.difficulty).toLowerCase()}`}>{entry.difficulty}</span> : null}
        </div>
        {entry.modes && entry.modes.length ? (
          <div className="party-card-modes" aria-label="Modes">{entry.modes.slice(0, 4).map((mode) => <span key={mode.id} className="party-tag">{mode.label}</span>)}{entry.modes.length > 4 ? <span className="party-tag">+{entry.modes.length - 4}</span> : null}</div>
        ) : null}
        <div className="party-card-actions">
          <button type="button" className="party-btn" onClick={() => onHost(entry)} data-testid={`host-${entry.key}`}>{entry.kind === 'watch' ? 'Start' : 'Host'}</button>
          {openCount ? <button type="button" className="party-btn ghost" onClick={() => onJoin(entry)}>{entry.kind === 'watch' ? `Watching now (${openCount})` : `Join (${openCount})`}</button> : null}
          <button type="button" className="party-btn ghost" onClick={() => onRules(entry)}>How to play</button>
        </div>
      </div>
    </article>
  );
}

const STATUS_TEXT = { lobby: 'In lobby', playing: 'Playing', finished: 'Finished', live: 'Watching now' };

function SessionRow({ item, onOpen }) {
  const canJoinAsPlayer = item.kind === 'watch' ? true : item.status === 'lobby'
    ? item.players.length < item.maxPlayers
    : item.kind === 'party' && item.allowLateJoin && item.players.length < item.maxPlayers;
  return (
    <li className="party-session-row" style={{ '--party-accent': item.accent || '#5865f2' }} data-testid={`session-row-${item.id}`}>
      <span className="party-session-icon" aria-hidden="true">{item.icon}</span>
      <div className="party-session-info">
        <strong>
          {item.name}
          {item.invited ? <span className="party-tag">Invited</span> : null}
          {item.member ? <span className="party-tag">Joined</span> : null}
          {item.visibility === 'friends' && !item.member ? <span className="party-tag">Friend</span> : null}
        </strong>
        <span>
          <UserAvatar userId={item.hostUserId} name={item.hostName} size={16} /> {item.hostName} · {item.kind === 'watch' ? `${item.players.length} watching${item.nowPlaying && item.nowPlaying.title ? ` · ${item.nowPlaying.title}` : ''}` : `${item.players.length}/${item.maxPlayers} players · ${STATUS_TEXT[item.status] || item.status}`}
          {item.players.length ? <span style={{ display: 'inline-flex', gap: 2, marginLeft: 6, verticalAlign: 'middle' }}>{item.players.slice(0, 6).map((p) => <UserAvatar key={p.userId} userId={p.userId} name={p.name} size={16} />)}</span> : null}
        </span>
      </div>
      {item.member ? <button type="button" className="party-btn small" onClick={() => onOpen(item, 'open')}>Open</button> : (
        <span className="party-row">
          {canJoinAsPlayer ? <button type="button" className="party-btn small" onClick={() => onOpen(item, 'join')}>Join</button> : null}
          {item.kind === 'party' && item.allowSpectators ? <button type="button" className="party-btn small ghost" onClick={() => onOpen(item, 'spectate')}>Watch</button> : null}
        </span>
      )}
    </li>
  );
}

function StatsPanel({ stats, entries }) {
  if (!stats || !stats.totals || !stats.totals.games_played) return null;
  const favourite = entries.find((entry) => entry.modeId === stats.favourite);
  const t = stats.totals;
  return (
    <section className="party-panel stats" data-testid="party-stats">
      <div className="party-panel-title">Your drawing game stats</div>
      <div className="party-my-stats">
        <div><strong>{t.games_played}</strong><span>Games</span></div>
        <div><strong>{t.games_won}</strong><span>Wins</span></div>
        <div><strong>{t.total_points}</strong><span>Points</span></div>
        <div><strong>{t.correct_guesses}</strong><span>Correct guesses</span></div>
        <div><strong>{t.drawings}</strong><span>Drawings</span></div>
        <div><strong>{t.votes_received}</strong><span>Votes received</span></div>
      </div>
      {favourite ? <div className="party-muted">Favourite: {entryIcon(favourite)} {favourite.name}</div> : null}
    </section>
  );
}

export default function GamesCatalogue({
  entries = [],
  sessions = [],
  stats = null,
  recent = [],
  serverName = '',
  initialCategory = 'All',
  joining = false,
  onHost,
  onOpenSession,
  onJoinCode,
  onOpenResult,
}) {
  const [selection, setSelection] = useState(() => initialSelection(initialCategory));
  const { group, sub } = selection;
  const [rules, setRules] = useState(null);
  const [code, setCode] = useState('');
  const groups = useMemo(() => {
    const present = new Set(entries.map(groupOf));
    return GROUPS.filter((name) => name === 'All Activities' || present.has(name));
  }, [entries]);
  const gameCategories = useMemo(() => {
    const present = new Set(entries.filter((entry) => groupOf(entry) === 'Games').map((entry) => entry.category));
    return GAME_CATEGORIES.filter((name) => present.has(name)).concat(Array.from(present).filter((name) => !GAME_CATEGORIES.includes(name)));
  }, [entries]);
  const visible = entries.filter((entry) => (group === 'All Activities' || groupOf(entry) === group) && (!sub || entry.category === sub));
  const mine = sessions.filter((item) => item.member);
  const open = sessions.filter((item) => !item.member);
  const openCounts = useMemo(() => {
    const counts = {};
    open.forEach((item) => {
      counts[item.entryKey] = (counts[item.entryKey] || 0) + 1;
    });
    return counts;
  }, [open]);

  const submitCode = (event) => {
    event.preventDefault();
    const value = code.trim();
    if (!value) return;
    Promise.resolve(onJoinCode(value)).then((ok) => {
      if (ok) setCode('');
    });
  };
  const joinFirst = (entry) => {
    const first = open.find((item) => item.entryKey === entry.key);
    if (first) onOpenSession(first, first.status === 'lobby' ? 'join' : 'spectate');
  };

  return (
    <div className="party-arcade" data-testid="games-tab">
      <header className="party-arcade-hero">
        <div>
          <div className="party-kicker">Voxemera{serverName ? ` · ${serverName}` : ''}</div>
          <h1>Activities</h1>
          <p>Games, Texas Hold'em, Watch Together and drawing games on the real Canvas editor. Start one and invite people with a code; it keeps running while you chat.</p>
        </div>
        <form className="party-join-code" onSubmit={submitCode}>
          <label htmlFor="games-code-input">Have a code?</label>
          <div className="party-row">
            <input id="games-code-input" value={code} onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))} placeholder="ABC123" autoComplete="off" aria-label="Invite code" data-testid="join-code-input" />
            <button type="submit" className="party-btn" disabled={joining || !code.trim()} data-testid="join-code-submit">{joining ? 'Joining…' : 'Join'}</button>
          </div>
        </form>
      </header>

      {mine.length ? (
        <section className="party-section" data-testid="my-games">
          <h2>Your activities</h2>
          <ul className="party-session-list">{mine.map((item) => <SessionRow key={`${item.kind}-${item.id}`} item={item} onOpen={onOpenSession} />)}</ul>
        </section>
      ) : null}

      <section className="party-section" data-testid="open-games">
        <h2>{serverName ? `Open in ${serverName}` : 'Activities you can join'}</h2>
        {open.length
          ? <ul className="party-session-list">{open.map((item) => <SessionRow key={`${item.kind}-${item.id}`} item={item} onOpen={onOpenSession} />)}</ul>
          : <p className="party-muted">Nothing open right now. Start an activity and everyone in {serverName || 'your server'} can join.</p>}
      </section>

      <section className="party-section">
        <div className="party-section-head">
          <h2>Pick an activity</h2>
          <div className="party-chips" role="tablist" aria-label="Activity groups">
            {groups.map((name) => <button key={name} type="button" role="tab" aria-selected={group === name} className={`party-chip${group === name ? ' active' : ''}`} onClick={() => setSelection({ group: name, sub: '' })}>{name}</button>)}
          </div>
        </div>
        {group === 'Games' && gameCategories.length > 1 ? (
          <div className="party-chips" role="tablist" aria-label="Kinds of game" style={{ margin: '-4px 0 12px' }}>
            <button type="button" role="tab" aria-selected={!sub} className={`party-chip${!sub ? ' active' : ''}`} onClick={() => setSelection({ group, sub: '' })}>All games</button>
            {gameCategories.map((name) => <button key={name} type="button" role="tab" aria-selected={sub === name} className={`party-chip${sub === name ? ' active' : ''}`} onClick={() => setSelection({ group, sub: name })}>{name}</button>)}
          </div>
        ) : null}
        <div className="party-card-grid">
          {visible.map((entry, index) => <GameCard key={entry.key} entry={entry} index={index} openCount={openCounts[entry.key] || 0} onHost={onHost} onRules={setRules} onJoin={joinFirst} />)}
        </div>
        <p className="party-muted small">Board, card and real-time games are adapted from <a href="https://github.com/absswds/GameNest" target="_blank" rel="noreferrer">GameNest</a> (Apache-2.0). Texas Hold'em is adapted from <a href="https://github.com/playpip/pip-web" target="_blank" rel="noreferrer">Pip</a> (MIT). Arcade games run in the host player's browser and connect players directly (<a href="/arcade/NOTICE.txt" target="_blank" rel="noreferrer">credits</a>).</p>
      </section>

      <div className="party-arcade-bottom">
        <StatsPanel stats={stats} entries={entries} />
        {recent.length ? (
          <section className="party-panel">
            <div className="party-panel-title">Recent galleries</div>
            <ul className="party-recent">
              {recent.slice(0, 8).map((result) => {
                const entry = entries.find((candidate) => candidate.modeId === result.modeId);
                return (
                  <li key={result.id}>
                    <button type="button" className="party-link" onClick={() => onOpenResult(result.id)}>
                      {entry ? entryIcon(entry) : '🎨'} {entry ? entry.name : result.modeId} · #{result.rank} of {result.players} · {result.score} pts
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>

      {rules ? <RulesDialog entry={rules} onClose={() => setRules(null)} onHost={(entry) => { setRules(null); onHost(entry); }} /> : null}
    </div>
  );
}
